import { computed, EventEmitter, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AppConstants } from '@shared-config/appConstants';
import { AppHelper } from '@shared-helpers/appHelper';
import { DateConverter } from '@shared-helpers/dateConverter';
import { ExpertModel } from '@shared-model/expert/ExpertModel';
import { ExpertReviewModel } from '@shared-model/expert/ExpertReviewModel';
import { ExpertSearchCriteria } from '@shared-model/expert/ExpertSearchCriteria';
import { ExpertTimestampModel } from '@shared-model/expert/ExpertTimestampModel';
import { ServiceOperationOutcome } from '@shared-results/ServiceOperationOutcome';
import { ValidationOutcome } from '@shared-results/ValidationOutcome';
import { ExpertListResult } from '@shared-results/expert/ExpertListResult';
import { ExpertRatingResult } from '@shared-results/expert/ExpertRatingResult';
import { ExpertReviewListResult } from '@shared-results/expert/ExpertReviewListResult';
import { ExpertStatusListResult } from '@shared-results/expert/ExpertStatusListResult';
import { BasePollingService } from '@shared-services/base-polling-service';
import { Observable, Observer, Subject } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';

@Injectable({
  providedIn: 'root' // Single instance provided in root
})
export class ExpertService extends BasePollingService<ExpertStatusListResult, ExpertStatusListResult> {
  private expertUrl = '/expert/';
  private expertCache: Map<number, string> = new Map<number, string>();
  private recentlyOfflineExpertsExpiryTime: number = 60000;
  private latestExpertStatusResult: ExpertStatusListResult = null;
  signalExpertStatusPollingResult$: Subject<ExpertStatusListResult> = new Subject<ExpertStatusListResult>();
  signalExpertStatusPollingError$: Subject<any> = new Subject<any>();
  signalExpertReviewAdded$ = new EventEmitter<ExpertReviewModel>();

  // Should these be scoped per tile grid instead?
  activeExpertId: WritableSignal<number> = signal(null, { equal: AppHelper.objectsEqual });
  activeExpertIdObservable = toObservable(this.activeExpertId);

  private recentlyOfflineExpertList: WritableSignal<ExpertTimestampModel[]> = signal([], { equal: AppHelper.objectsEqual });
  recentlyOfflineExpertIds: Signal<number[]> = computed(() =>
    this.recentlyOfflineExpertList()?.filter(et => (Date.now() - et?.TimeStamp?.getTime()) < this.recentlyOfflineExpertsExpiryTime)?.map(et => et.ExpertId)
    , { equal: AppHelper.objectsEqual });

  addRecentlyOfflineExperts(expertIds: number[]) {
    if (expertIds?.length > 0) {
      //console.log(`addRecentlyOfflineExperts ${expertIds}`);
      expertIds?.forEach(expertId =>
        this.recentlyOfflineExpertList.update(values =>
          [...values, new ExpertTimestampModel({ ExpertId: expertId, TimeStamp: new Date() })]
        )
      );
    }
  }

  removeRecentlyOfflineExperts(expertIds: number[]) {
    if (expertIds?.length > 0 && this.recentlyOfflineExpertList().some(et => expertIds.includes(et.ExpertId))) {
      //console.log(`removeRecentlyOfflineExperts ${expertIds}`);
      this.recentlyOfflineExpertList?.update(values => values?.filter(et => !expertIds?.includes(et.ExpertId)));
    }
  }

  removeExpiredRecentlyOfflineExperts() {
    let originalList = this.recentlyOfflineExpertList();
    let originalLength = originalList?.length;
    if (originalLength > 0) {
      // There is at least one entry in the list
      // Calculate new list removing any expired entries
      let filtered = originalList?.filter(et => (Date.now() - et?.TimeStamp?.getTime()) < this.recentlyOfflineExpertsExpiryTime);
      if (filtered?.length != originalLength) {
        // Length of list has changed, update the list
        this.recentlyOfflineExpertList.update(values => filtered);
      }
    }
  }

  expertIsRecentlyOffline(expertId: number) {
    return this.recentlyOfflineExpertIds()?.includes(expertId);
  }

  constructor() { super("ExpertService"); }

  protected override getPollingObservable(): Observable<ExpertStatusListResult> {
    let url: string = `${this.expertUrl}GetExpertStatuses`;
    let body: string = null;// expertIds ? JSON.stringify({ expertIds: expertIds }) : null;
    let options = this.httpOptions;
    return this.http?.post<ExpertStatusListResult>(url, body, options).pipe(
      timeout(this.httpTimeout),
      map((item) => DateConverter.stringsToDates(item)),
      catchError(this.errorService?.handleHttpErrorAndRethrow<ExpertStatusListResult>(url, body, options, this)),
      switchMap((result) => {
        return this.handleOperationResult(url, body, options, result, {
          throwIfNotSuccess: true
        })
      }),
    );
  }

  protected override getPollingInterval(): number {
    return AppConstants.statusPollingInterval;
  }

  protected override onPollingError(error: any): void {
    this.signalExpertStatusPollingError$.next(error);
    this.signalExpertStatusPollingResult$.next(null);
  }

  protected override onPollingResult(result: ExpertStatusListResult): ExpertStatusListResult {
    this.signalExpertStatusPollingResult$.next(result);
    this.signalExpertStatusPollingError$.next(null);
    return result;
  }

  protected override getPollingCachedData(): ExpertStatusListResult {
    return this.latestExpertStatusResult;
  }

  protected override setPollingCachedData(data: ExpertStatusListResult): void {
    this.latestExpertStatusResult = data;
  }

  private cacheExpertData<TExpert extends ExpertModel>(...experts: TExpert[]): void {
    // Cache subset of all expert data we see
    // Start off with just nickname - can be extended to add more data if needed
    experts?.forEach((expert: TExpert) => { this.expertCache?.set(expert?.ExpertId, expert?.Nickname); });
  }

  tryGetExpertNicknameFromCache(expertId: number): string {
    return this.expertCache?.get(expertId);
  }

  getExpertsByIds(expertsToGet: number[], thumbnailImageWidth?: number, thumbnailImageHeight?: number, observerOrNext?: Partial<Observer<ExpertListResult>> | ((value: ExpertListResult) => void)): void {
    let url: string = `${this.expertUrl}GetExpertsByIds`;
    let body: string = JSON.stringify({ expertsIds: expertsToGet, thumbnailImageWidth: thumbnailImageWidth, thumbnailImageHeight: thumbnailImageHeight });
    let options = this.httpOptions;
    let client = this.http?.post<ExpertListResult>(url, body, options).pipe(
      timeout(this.httpTimeout),
      map((item) => DateConverter.stringsToDates(item)),
      catchError(this.errorService?.handleHttpError<ExpertListResult>(url, body, options, this))
    );
    this.addSubscriptions(client, observerOrNext, (result) => {
      if (result?.ServiceOperationOutcome == ServiceOperationOutcome.Success) {
        // Request succeeded
        this.cacheExpertData(...result?.Experts);
      }
    });
  }

  getExperts(filters: ExpertSearchCriteria, observerOrNext?: Partial<Observer<ExpertListResult>> | ((value: ExpertListResult) => void)): void {
    let url: string = `${this.expertUrl}getExperts`;
    let body: string = JSON.stringify({ filters: filters });
    let options = this.httpOptions;
    let client = this.http?.post<ExpertListResult>(url, body, options).pipe(
      timeout(this.httpTimeout),
      map((item) => DateConverter.stringsToDates(item)),
      catchError(this.errorService?.handleHttpError<ExpertListResult>(url, body, options, this))
    );
    this.addSubscriptions(client, observerOrNext, (result) => {
      if (result?.ServiceOperationOutcome == ServiceOperationOutcome.Success) {
        // Request succeeded
        this.cacheExpertData(...result?.Experts);
      }
    });
  }

  searchExperts(key: string, observerOrNext?: Partial<Observer<ExpertListResult>> | ((value: ExpertListResult) => void)): void {
    let url: string = `${this.expertUrl}SearchExperts`;
    let body: string = JSON.stringify({ key: key });
    let options = this.httpOptions;
    let client = this.http?.post<ExpertListResult>(url, body, options).pipe(
      timeout(this.httpTimeout),
      map((item) => DateConverter.stringsToDates(item)),
      catchError(this.errorService?.handleHttpError<ExpertListResult>(url, body, options, this))
    );
    this.addSubscriptions(client, observerOrNext, (result) => {
      if (result?.ServiceOperationOutcome == ServiceOperationOutcome.Success) {
        // Request succeeded
        this.cacheExpertData(...result?.Experts);
      }
    });
  }

  getExpertReviews(expertId: number, reviewsCount: number, page: number, observerOrNext?: Partial<Observer<ExpertReviewListResult>> | ((value: ExpertReviewListResult) => void)): void {
    let url: string = `${this.expertUrl}GetExpertReviews`;
    let body: string = JSON.stringify({ expertId: expertId, reviewsCount: reviewsCount, page: page });
    let options = this.httpOptions;
    this.http?.post<ExpertReviewListResult>(url, body, options).pipe(
      timeout(this.httpTimeout),
      switchMap((result) => { return this.throwErrorIfNotSuccess(url, body, options, result) }),
      map((item) => DateConverter.stringsToDates(item)),
      catchError(this.errorService?.handleHttpError<ExpertReviewListResult>(url, body, options, this))
    ).subscribe(observerOrNext);
  }
  setExpertReview(review: ExpertReviewModel, observerOrNext?: Partial<Observer<ExpertRatingResult>> | ((value: ExpertRatingResult) => void)): void {
    let url: string = `${this.expertUrl}SetExpertReview`;
    let body: string = JSON.stringify({ review: review });
    let options = this.httpOptions;
    let client = this.http?.post<ExpertRatingResult>(url, body, options).pipe(
      timeout(this.httpTimeout),
      map((item) => DateConverter.stringsToDates(item)),
      catchError(this.errorService?.handleHttpError<ExpertRatingResult>(url, body, options, this))
    );
    this.addSubscriptions(client, observerOrNext, (result) => {
      if (result?.ServiceOperationOutcome == ServiceOperationOutcome.Success &&
        result.MemberDisplayNameValidationOutcome == Number(ValidationOutcome.Valid) &&
        result.ReviewRatingValidationOutcome == Number(ValidationOutcome.Valid) &&
        result.ReviewTextValidationOutcome == Number(ValidationOutcome.Valid) &&
        result.ReviewTitleValidationOutcome == Number(ValidationOutcome.Valid)) {
        // Request succeeded
        if (review) {
          // Set data returned by server
          review.AuthorDisplayName = result.MemberDisplayName;
          review.Created = result.ReviewCreated;
          // Signal that new review added
          this.signalExpertReviewAdded$.emit(review);
        }
      }
    });
  }

}
